// API client for OurSS backend

const API_BASE = '/api';

interface RequestOptions extends RequestInit {
  token?: string;
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { token, ...fetchOptions } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...fetchOptions.headers as Record<string, string>,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...fetchOptions,
    headers,
  });

  if (!response.ok) {
    // Handle 401 Unauthorized - token expired or invalid
    // Only trigger logout/redirect if this was an authenticated request (had a token)
    if (response.status === 401 && token) {
      // Import auth store dynamically to avoid circular dependency
      const { useAuthStore } = await import('../store/useStore');
      const clearAuth = useAuthStore.getState().clearAuth;
      clearAuth();

      // Redirect to login page
      window.location.href = '/login';

      throw new Error('Session expired. Please login again.');
    }

    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

// Auth
export const auth = {
  register: (data: { username: string; email: string; password: string; displayName?: string; inviteCode: string }) =>
    request<{ user: any; token: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  login: (data: { usernameOrEmail: string; password: string }) =>
    request<{ user: any; token: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getMe: (token: string) => request<any>('/auth/me', { token }),

  updateProfile: (token: string, data: { displayName?: string; bio?: string; avatar?: string; defaultPublicShares?: boolean }) =>
    request<any>('/auth/profile', {
      method: 'PATCH',
      token,
      body: JSON.stringify(data),
    }),

  uploadAvatar: async (token: string, file: File) => {
    const formData = new FormData();
    formData.append('avatar', file);

    const res = await fetch(`${API_BASE}/auth/avatar`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || 'Upload failed');
    }

    return res.json();
  },

  updatePassword: (token: string, data: { currentPassword: string; newPassword: string }) =>
    request<void>('/auth/password', {
      method: 'PATCH',
      token,
      body: JSON.stringify(data),
    }),

  markWelcomeTourSeen: (token: string) =>
    request<{ success: boolean }>('/auth/welcome-tour-seen', {
      method: 'POST',
      token,
    }),

  markMastodonFeatureSeen: (token: string) =>
    request<{ success: boolean }>('/auth/mastodon-feature-seen', {
      method: 'POST',
      token,
    }),

  forgotPassword: (data: { email: string }) =>
    request<{ message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  resetPassword: (data: { token: string; newPassword: string }) =>
    request<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// Feeds
export const feeds = {
  getAll: (token: string) => request<any[]>('/feeds', { token }),

  subscribe: (token: string, data: { url: string; folderId?: string; customTitle?: string }) =>
    request<any>('/feeds/subscribe', {
      method: 'POST',
      token,
      body: JSON.stringify(data),
    }),

  unsubscribe: (token: string, subscriptionId: string) =>
    request<void>(`/feeds/${subscriptionId}`, {
      method: 'DELETE',
      token,
    }),

  update: (token: string, subscriptionId: string, data: { title?: string; folderId?: string | null }) =>
    request<any>(`/feeds/${subscriptionId}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify(data),
    }),

  refresh: (token: string, feedId: string) =>
    request<{ success: boolean; articlesAdded: number }>(`/feeds/${feedId}/refresh`, {
      method: 'POST',
      token,
    }),

  detect: (token: string, url: string) =>
    request<{ feeds: Array<{ url: string; title: string; description?: string }> }>('/feeds/detect', {
      method: 'POST',
      token,
      body: JSON.stringify({ url }),
    }),

  importOpml: async (token: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch('/api/feeds/import-opml', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  },

  exportOpml: async (token: string) => {
    const response = await fetch('/api/feeds/export-opml', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ourss-feeds-${new Date().toISOString().split('T')[0]}.opml`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },
};

// Articles
export const articles = {
  getAll: (
    token: string,
    params?: {
      limit?: number;
      offset?: number;
      unread?: boolean;
      liked?: boolean;
      feedId?: string;
      folderId?: string;
      search?: string;
      archive?: 'true' | 'false' | 'all';
      bucket?: 'today' | 'recent' | 'fading';
    }
  ) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
    }
    return request<{ articles: any[]; total: number; limit: number; offset: number }>(
      `/articles?${queryParams}`,
      { token }
    );
  },

  getOne: (token: string, id: string) => request<any>(`/articles/${id}`, { token }),

  markRead: (token: string, id: string) =>
    request<void>(`/articles/${id}/read`, { method: 'POST', token }),

  markUnread: (token: string, id: string) =>
    request<void>(`/articles/${id}/read`, { method: 'DELETE', token }),

  star: (token: string, id: string) =>
    request<void>(`/articles/${id}/star`, { method: 'POST', token }),

  unstar: (token: string, id: string) =>
    request<void>(`/articles/${id}/star`, { method: 'DELETE', token }),

  like: (token: string, id: string) =>
    request<void>(`/articles/${id}/like`, { method: 'POST', token }),

  unlike: (token: string, id: string) =>
    request<void>(`/articles/${id}/like`, { method: 'DELETE', token }),

  getSubscription: (token: string, id: string) =>
    request<{ subscribed: boolean }>(`/articles/${id}/subscription`, { token }),

  subscribe: (token: string, id: string) =>
    request<{ message: string }>(`/articles/${id}/subscribe`, { method: 'POST', token }),

  unsubscribe: (token: string, id: string) =>
    request<void>(`/articles/${id}/unsubscribe`, { method: 'DELETE', token }),

  markAllRead: (
    token: string,
    scope: { feedId?: string; folderId?: string; bucket?: 'today' | 'recent' | 'fading'; beforeDate?: string } = {}
  ) => {
    const queryParams = new URLSearchParams();
    if (scope.feedId) queryParams.append('feedId', scope.feedId);
    if (scope.folderId) queryParams.append('folderId', scope.folderId);
    if (scope.bucket) queryParams.append('bucket', scope.bucket);
    if (scope.beforeDate) queryParams.append('beforeDate', scope.beforeDate);
    return request<{ markedCount: number }>(`/articles/mark-all-read?${queryParams}`, {
      method: 'POST',
      token,
    });
  },

  // One request for a whole page of the paged Read list
  markReadBatch: (token: string, articleIds: string[]) =>
    request<{ markedCount: number }>('/articles/mark-read', {
      method: 'POST',
      token,
      body: JSON.stringify({ articleIds }),
    }),

  markUnreadBatch: (token: string, articleIds: string[]) =>
    request<{ unmarkedCount: number }>('/articles/mark-unread', {
      method: 'POST',
      token,
      body: JSON.stringify({ articleIds }),
    }),

  getUnreadCount: (token: string, feedId?: string, folderId?: string) => {
    const queryParams = new URLSearchParams();
    if (feedId) queryParams.append('feedId', feedId);
    if (folderId) queryParams.append('folderId', folderId);
    return request<{ count: number }>(`/articles/unread-count?${queryParams}`, { token });
  },
};

// Comments
export const comments = {
  create: (token: string, data: { articleId: string; content: string; mentions?: string[] }) =>
    request<any>('/comments', {
      method: 'POST',
      token,
      body: JSON.stringify(data),
    }),

  getForArticle: (token: string, articleId: string) =>
    request<any[]>(`/comments/article/${articleId}`, { token }),

  update: (token: string, id: string, content: string) =>
    request<any>(`/comments/${id}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify({ content }),
    }),

  delete: (token: string, id: string) =>
    request<void>(`/comments/${id}`, { method: 'DELETE', token }),
};

// Social
export const social = {
  share: (token: string, data: { articleId: string; note?: string; isPublic?: boolean; mentions?: string[] }) =>
    request<any>('/social/share', {
      method: 'POST',
      token,
      body: JSON.stringify(data),
    }),

  getShares: (token: string, params?: { limit?: number; offset?: number }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
    }
    return request<any[]>(`/social/shares?${queryParams}`, { token });
  },

  deleteShare: (token: string, id: string) =>
    request<void>(`/social/share/${id}`, { method: 'DELETE', token }),

  follow: (token: string, userId: string) =>
    request<void>(`/social/follow/${userId}`, { method: 'POST', token }),

  unfollow: (token: string, userId: string) =>
    request<void>(`/social/follow/${userId}`, { method: 'DELETE', token }),

  getFollowing: (token: string) => request<any[]>('/social/following', { token }),

  getFollowers: (token: string) => request<any[]>('/social/followers', { token }),

  searchUsers: (token: string, query: string, limit?: number) => {
    const params = new URLSearchParams({ q: query });
    if (limit) params.append('limit', String(limit));
    return request<any[]>(`/social/users/search?${params}`, { token });
  },

  getAllUsers: (token: string) => request<any[]>('/social/users', { token }),

  getUserShares: (token: string, userId: string, params?: { limit?: number; offset?: number }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
    }
    return request<{ user: any; shares: any[] }>(`/social/users/${userId}/shares?${queryParams}`, { token });
  },
};

// Notifications
export const notifications = {
  getAll: (token: string, params?: { limit?: number; offset?: number; unreadOnly?: boolean }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
    }
    return request<{ notifications: any[]; unreadCount: number }>(`/notifications?${queryParams}`, { token });
  },

  markRead: (token: string, id: string) =>
    request<void>(`/notifications/${id}/read`, { method: 'POST', token }),

  markAllRead: (token: string) =>
    request<void>('/notifications/mark-all-read', { method: 'POST', token }),
};

// Folders
export const folders = {
  getAll: (token: string) => request<any[]>('/folders', { token }),

  create: (token: string, data: { name: string; parentId?: string }) =>
    request<any>('/folders', {
      method: 'POST',
      token,
      body: JSON.stringify(data),
    }),

  update: (token: string, id: string, data: { name?: string; parentId?: string | null }) =>
    request<any>(`/folders/${id}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify(data),
    }),

  delete: (token: string, id: string) =>
    request<void>(`/folders/${id}`, { method: 'DELETE', token }),

  moveSubscription: (token: string, folderId: string, subscriptionId: string) =>
    request<void>(`/folders/${folderId}/move-subscription`, {
      method: 'POST',
      token,
      body: JSON.stringify({ subscriptionId }),
    }),

  removeSubscription: (token: string, subscriptionId: string) =>
    request<void>('/folders/remove-subscription', {
      method: 'POST',
      token,
      body: JSON.stringify({ subscriptionId }),
    }),
};

// Instance
export const instance = {
  getInfo: () => request<{
    name: string;
    description?: string;
    allowRegistration: boolean;
    version: string;
    userCount?: number;
  }>('/instance'),
};

// Collaborative Posts
export const collaborative = {
  getOpportunities: (token: string) => request<any[]>('/collaborative/opportunities', { token }),

  createPost: (token: string, data: {
    triggerArticleId: string;
    title: string;
    summary?: string;
    inviteUserIds?: string[]
  }) =>
    request<any>('/collaborative/posts', {
      method: 'POST',
      token,
      body: JSON.stringify(data),
    }),

  getPosts: (token: string, params?: { status?: 'draft' | 'published' | 'all'; myContributions?: boolean }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
    }
    return request<any[]>(`/collaborative/posts?${queryParams}`, { token });
  },

  getPost: (token: string, id: string) => request<any>(`/collaborative/posts/${id}`, { token }),

  joinPost: (token: string, id: string) =>
    request<any>(`/collaborative/posts/${id}/join`, { method: 'POST', token }),

  addSection: (token: string, postId: string, content: string) =>
    request<any>(`/collaborative/posts/${postId}/sections`, {
      method: 'POST',
      token,
      body: JSON.stringify({ content }),
    }),

  updateSection: (token: string, postId: string, sectionId: string, content: string) =>
    request<any>(`/collaborative/posts/${postId}/sections/${sectionId}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify({ content }),
    }),

  deleteSection: (token: string, postId: string, sectionId: string) =>
    request<void>(`/collaborative/posts/${postId}/sections/${sectionId}`, {
      method: 'DELETE',
      token
    }),

  publishPost: (token: string, id: string, isPublic: boolean) =>
    request<any>(`/collaborative/posts/${id}/publish`, {
      method: 'PATCH',
      token,
      body: JSON.stringify({ isPublic }),
    }),
};

// Public API (no authentication required)
export const publicApi = {
  getInstance: () =>
    request<{
      name: string;
      description: string;
      allowRegistration: boolean;
      version: string;
      userCount: number;
    }>('/instance'),

  getPublicShares: (params?: { limit?: number; offset?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());
    const query = searchParams.toString();
    return request<{
      shares: Array<{
        id: string;
        note: string | null;
        isPublic: boolean;
        createdAt: string;
        user: {
          id: string;
          username: string;
          displayName: string | null;
          avatar: string | null;
        };
        article: {
          id: string;
          title: string;
          url: string;
          excerpt: string | null;
          content: string;
          imageUrl: string | null;
          publishedAt: string | null;
          feed: {
            id: string;
            title: string;
            siteUrl: string | null;
          };
          _count: {
            comments: number;
            shares: number;
            likes: number;
          };
        };
      }>;
      pagination: {
        total: number;
        limit: number;
        offset: number;
        hasMore: boolean;
      };
    }>(`/social/public-shares${query ? `?${query}` : ''}`);
  },

  getPublicPosts: (params?: { limit?: number; offset?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());
    const query = searchParams.toString();
    return request<{
      posts: Array<{
        id: string;
        title: string;
        summary: string | null;
        status: string;
        isPublic: boolean;
        publishedAt: string | null;
        triggerArticle: {
          id: string;
          title: string;
          url: string;
          excerpt: string | null;
          imageUrl: string | null;
          feed: {
            id: string;
            title: string;
          };
        };
        authors: Array<{
          id: string;
          role: string;
          user: {
            id: string;
            username: string;
            displayName: string | null;
            avatar: string | null;
          };
        }>;
        sections: Array<{
          id: string;
          authorId: string;
          order: number;
        }>;
      }>;
      pagination: {
        total: number;
        limit: number;
        offset: number;
        hasMore: boolean;
      };
    }>(`/collaborative/public-posts${query ? `?${query}` : ''}`);
  },

  getPublicUsers: () =>
    request<{
      users: Array<{
        id: string;
        username: string;
        displayName: string | null;
        avatar: string | null;
        bio: string | null;
        publicShareCount: number;
      }>;
    }>('/social/public-users'),

  getPublicPost: (id: string) =>
    request<{
      id: string;
      title: string;
      summary: string | null;
      status: string;
      isPublic: boolean;
      publishedAt: string | null;
      createdAt: string;
      triggerArticle: {
        id: string;
        title: string;
        url: string;
        excerpt: string | null;
        content: string;
        imageUrl: string | null;
        publishedAt: string | null;
        feed: {
          id: string;
          title: string;
          siteUrl: string | null;
        };
      };
      authors: Array<{
        id: string;
        role: string;
        user: {
          id: string;
          username: string;
          displayName: string | null;
          avatar: string | null;
          bio: string | null;
        };
      }>;
      sections: Array<{
        id: string;
        content: string;
        order: number;
        author: {
          id: string;
          username: string;
          displayName: string | null;
          avatar: string | null;
        };
      }>;
    }>(`/collaborative/public-posts/${id}`),
};

// Mastodon Integration
export const mastodon = {
  // Start OAuth connection flow
  connect: (token: string, instance: string) =>
    request<{ authUrl: string }>('/mastodon/connect', {
      method: 'POST',
      token,
      body: JSON.stringify({ instance }),
    }),

  // Disconnect Mastodon account
  disconnect: (token: string) =>
    request<{ success: boolean }>('/mastodon/disconnect', {
      method: 'DELETE',
      token,
    }),

  // Update auto-post setting
  updateSettings: (token: string, autoPost: boolean) =>
    request<{ success: boolean; autoPost: boolean }>('/mastodon/settings', {
      method: 'PATCH',
      token,
      body: JSON.stringify({ autoPost }),
    }),

  // Get current Mastodon connection status
  getStatus: (token: string) =>
    request<{
      connected: boolean;
      instance?: string;
      username?: string;
      autoPost?: boolean;
      error?: string;
    }>('/mastodon/status', { token }),
};

// Announcements
export interface Announcement {
  id: string;
  title: string;
  content: string;
  type: 'modal' | 'notification';
  priority: number;
  isActive: boolean;
  startsAt: string;
  expiresAt: string | null;
  targetRole: string | null;
  createdAt: string;
  updatedAt?: string;
  createdBy: {
    id: string;
    username: string;
    displayName: string | null;
  };
  _count?: {
    dismissals: number;
  };
}

export const announcements = {
  // Get active announcements for the current user
  getActive: (token: string) =>
    request<{ announcements: Announcement[] }>('/announcements/active', { token }),

  // Dismiss an announcement
  dismiss: (token: string, id: string) =>
    request<{ success: boolean }>(`/announcements/${id}/dismiss`, {
      method: 'POST',
      token,
    }),
};

// Admin Announcements
export const adminAnnouncements = {
  // Get all announcements (admin only)
  getAll: (token: string, params?: { active?: boolean }) => {
    const queryParams = new URLSearchParams();
    if (params?.active !== undefined) {
      queryParams.append('active', String(params.active));
    }
    return request<{ announcements: Announcement[] }>(`/admin/announcements?${queryParams}`, { token });
  },

  // Create a new announcement
  create: (token: string, data: {
    title: string;
    content: string;
    type?: 'modal' | 'notification';
    priority?: number;
    isActive?: boolean;
    startsAt?: string;
    expiresAt?: string | null;
    targetRole?: 'user' | 'admin' | null;
  }) =>
    request<Announcement>('/admin/announcements', {
      method: 'POST',
      token,
      body: JSON.stringify(data),
    }),

  // Update an announcement
  update: (token: string, id: string, data: {
    title?: string;
    content?: string;
    type?: 'modal' | 'notification';
    priority?: number;
    isActive?: boolean;
    startsAt?: string;
    expiresAt?: string | null;
    targetRole?: 'user' | 'admin' | null;
  }) =>
    request<Announcement>(`/admin/announcements/${id}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify(data),
    }),

  // Delete an announcement
  delete: (token: string, id: string) =>
    request<void>(`/admin/announcements/${id}`, {
      method: 'DELETE',
      token,
    }),
};
